import React, { useContext, useEffect, useState } from 'react';
import { Box, Checkbox, TextField, Typography } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import PropTypes from 'prop-types';

import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import { setUpJournalForSubmit } from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create, find } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import { getAccountOptions } from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';

export default function QuickBookRowCreditCardPaymentTemplate({ transaction, onTransactionAction, onGetHelp }) {
  const isMobile = checkIsSmallScreen();
  const tracking = useSegmentTrack();
  const { organizationId, organizationName } = useContext(PersonContext);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [creditCardAccountOptions, setCreditCardAccountOptions] = useState([]);
  const [paymentAccountOptions, setPaymentAccountOptions] = useState([]);
  const [journal, setJournal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [matchingTransaction, setMatchingTransaction] = useState(false);
  const [shouldBookMatchingTransaction, setShouldBookMatchingTransaction] = useState(false);
  const [shouldAddRule, setShouldAddRule] = useState(false);

  // Attempts to find a matching transaction for imported transfers
  // based on transaction amount and date (this does not currently filter by account type)
  const findMatchingTransaction = async (journal, accountId) => {
    let transactionType;

    if (journal.debitLines[0].accountIdSelect) {
      transactionType = 'DEBIT';
    } else if (journal.creditLines[0].accountIdSelect) {
      transactionType = 'CREDIT';
    }

    const query = {
      organizationId: journal.organizationId,
      amount: journal.amount,
      yodleeType: transactionType,
      date: {
        $lte: moment(journal.date).add(3, 'days').format('YYYY-MM-DD'),
        $gte: moment(journal.date).subtract(3, 'days').format('YYYY-MM-DD'),
      },
      journalId: null,
      split: { $or: [false, null] },
      ignored: { $or: [false, null] },
      pending: false,
      deleted: false,
    };

    if (accountId) {
      query.accountId = accountId;
    }

    const result = await find('yodlee-transactions', { query });

    if (result.data.length === 1) {
      setShouldBookMatchingTransaction(true);
      setMatchingTransaction(result.data[0]);
      if (transactionType === 'CREDIT') {
        journal.debitLines[0].accountIdSelect = result.data[0].account;
      } else {
        journal.creditLines[0].accountIdSelect = result.data[0].account;
      }
    } else {
      setMatchingTransaction(null);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      return;
    }
    const getData = async () => {
      const creditCarAccounts = await getAccountOptions(organizationId, { type2: 'Credit Card' });
      const paymentAccounts = await getAccountOptions(organizationId, {
        type2: ['Bank', 'HELOC', 'Owner Funds'],
      });
      setCreditCardAccountOptions(creditCarAccounts);
      setPaymentAccountOptions(paymentAccounts);
      const journalObject = getManualBasicJournal();
      const { account, id, amount, date, description, yodleeType } = transaction;
      if (yodleeType === 'CREDIT') {
        journalObject.debitLines[0].accountIdSelect = account;
        journalObject.debitLines[0].yodleeTransactionId = id;
      } else if (yodleeType === 'DEBIT') {
        journalObject.creditLines[0].accountIdSelect = account;
        journalObject.creditLines[0].yodleeTransactionId = id;
      }
      journalObject.description = description;
      journalObject.date = date;
      journalObject.amount = amount;
      journalObject.organizationId = organizationId;
      journalObject.journalScopeSelect = { name: `${organizationName} Portfolio`, id: 'Portfolio' };

      await findMatchingTransaction(journalObject, null);

      setJournal(journalObject);
      setIsLoading(false);
    };
    getData();
  }, [isLoading, organizationId, organizationName, transaction]);

  const addCreditCardPayment = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const journalData = {
      journal,
      organizationId,
      direction: transaction.yodleeType === 'CREDIT' ? 'cash in' : 'cash out',
      transactionId: transaction.id,
      type: 'Credit Card Payment',
      bookAsRefund: false,
    };
    const journalSubmit = setUpJournalForSubmit(journalData);

    // Find matching transaction if applicable
    if (matchingTransaction && shouldBookMatchingTransaction) {
      if (matchingTransaction.yodleeType === 'CREDIT') {
        journalSubmit.debitLines[0].yodleeTransactionId = matchingTransaction.id;
      } else if (matchingTransaction.yodleeType === 'DEBIT') {
        journalSubmit.creditLines[0].yodleeTransactionId = matchingTransaction.id;
      }
    }

    try {
      await create('journals', journalSubmit);
      setIsSubmitting(false);
      onTransactionAction(shouldAddRule, journalSubmit);
    } catch (err) {
      setError(err);
      setIsSubmitting(false);
      return;
    }
  };

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <form onSubmit={addCreditCardPayment}>
      <Box className={classes.rootContainer}>
        <Box className={classes.container}>
          {!journal.debitLines[0].yodleeTransactionId && (
            <QuickBookRowAutocomplete
              className={classes.autoCompleteField}
              displaySubaccounts
              options={creditCardAccountOptions}
              onChange={(value) => {
                findMatchingTransaction(journal, value.id);
                setJournal({ ...journal, debitLines: [{ ...journal.debitLines[0], accountIdSelect: value }] });
              }}
              value={journal.debitLines[0].accountIdSelect}
              label="Credit Card Account"
              required
            />
          )}
          {!journal.creditLines[0].yodleeTransactionId && (
            <QuickBookRowAutocomplete
              className={classes.autoCompleteField}
              displaySubaccounts
              options={paymentAccountOptions}
              onChange={(value) => {
                findMatchingTransaction(journal, value.id);
                setJournal({ ...journal, creditLines: [{ ...journal.creditLines[0], accountIdSelect: value }] });
              }}
              value={journal.creditLines[0].accountIdSelect}
              label="Paid From Account"
              required
            />
          )}
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
        </Box>
        <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
        {matchingTransaction && (
          <Box>
            <Alert severity="info" className={classes.infoAlert}>
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle2" className={classes.matchingTransactionAlertText}>
                  We found a matching transaction. Leave the box checked to book it as well.
                </Typography>
                <Box marginRight="auto" display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    color="primary"
                    checked={shouldBookMatchingTransaction}
                    onClick={() => {
                      setShouldBookMatchingTransaction(!shouldBookMatchingTransaction);
                    }}
                  />
                  {`${matchingTransaction.date} - ${matchingTransaction.description}`}
                </Box>
              </Box>
            </Alert>
          </Box>
        )}
        <QuickBookRowNotesAndAttachment
          journal={journal}
          onChange={(newJournal) => {
            setJournal(newJournal);
          }}
        />
        {error && error.message ? (
          <Alert severity="error" className={classes.errorAlert}>
            {error.message}
          </Alert>
        ) : null}
        <Box className={classes.actionsContainer}>
          <Button
            variant="outlined"
            size="small"
            className={classes.helpButton}
            onClick={() => {
              tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
              onGetHelp();
            }}
          >
            <HelpOutline className={classes.helpIcon} />
            Get Help
          </Button>
          <Box className={classes.rowActionsSecondaryContainer}>
            <Box className={classes.checkboxContainer}>
              <Checkbox
                color="primary"
                onClick={() => {
                  tracking('create_rule clicked', { location: 'Inline Booking - Import Feed' });
                  setShouldAddRule(!shouldAddRule);
                }}
              />
              <Typography variant="body1">Create rule after saving</Typography>
            </Box>
            <Button
              variant="contained"
              className={classes.saveButton}
              size="small"
              fullWidth={false}
              type="submit"
              onClick={() => {
                tracking('save clicked', { location: 'Inline Booking - Import Feed' });
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

QuickBookRowCreditCardPaymentTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func.isRequired,
  onGetHelp: PropTypes.func.isRequired,
};
